import {
  ActionButtonProps,
  Box,
  BoxProps,
  ColorOptions,
  Form,
  LoaderNew,
  ModalDialog,
  Spacer,
  TextNew,
} from '@jfs/ui-new'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Dimensions } from 'react-native'
import { Dropdown } from '../../components/Dropdown'
import { List } from '../../components/List'
import { SearchFilter } from '../../components/SearchFilter'
import { useLazyApiData, useStores } from '../../data/store'
import { clean, isAccessible } from '../../utils'
import { DeviceListItem } from './DeviceListItem'
import SearchButton from './SearchButton'
import { searchFormData } from './searchFormData'

const FormBox = ({ children }: any) => {
  return <Box {...styles.formContainer}>{children}</Box>
}

const deviceType = [
  { id: 'VOICEBOX', value: 'VOICEBOX' },
  { id: 'SQR', value: 'SQR' },
]

const Devices = () => {
  const [mid, setMid] = useState('')
  const [inputKeys, setInputkeys] = useState<String[]>([])
  const [selectedDeviceType, setSelectedDeviceType] = useState('VOICEBOX')

  const [isLoading, setLoading] = useState(false)
  const { deviceMappings, searchDevices, delink, accessConfig, bulkLinkDevice, updateDeviceStatusAsync } = useStores(
    (store) => ({
      getAll: store.devices.getAllMappings,
      searchDevices: store.devices.searchDevices,
      deviceMappings: store.devices.getDeviceMappings(),
      delink: store.devices.delinkDevice,
      bulkLinkDevice: store.devices.bulkLinkDevice,
      accessConfig: store.user.getAccessConfig(['merchant_config', 'device_mapping']),
      updateDeviceStatusAsync: store.devices.updateDeviceStatusAsync,
    }),
  )
  const [lazySearchApi, lazySearchHandler] = useLazyApiData(searchDevices)
  const [lazyDelinkApi, { state }] = useLazyApiData(delink)
  const [lazyUpdateDeviceStatus, updateDeviceHandler] = useLazyApiData(updateDeviceStatusAsync)

  const [isValidDevice, setIsValidDevice] = useState(false)
  const [loadingIndex, setLoadingIndex] = useState(-1)
  const formRef = useRef(null)
  const formActionButtonRef = useRef<ActionButtonProps>()
  const onFormSubmit = useCallback(() => {
    formActionButtonRef?.current?.onPress()
  }, [])

  useEffect(() => {
    if (lazySearchHandler.state === 'pending') {
      setLoading(true)
    } else {
      setLoading(false)
    }
  }, [lazySearchHandler.state])
  const keyExtractor = (item: any): string => {
    return item.id
  }
  const headTitles = [
    { mid: 'MID' },
    { tid: 'TID' },
    { dsn: 'DSN' },
    { status: 'Status' },
    { mapping: 'Actions' },
    { linkedDeviceId: 'Paired Device Id' },
  ]
  const headTitlesWithoutAction = [
    { mid: 'MID' },
    { tid: 'TID' },
    { dsn: 'DSN' },
    { status: 'Status' },
    { linkedDeviceId: 'Paired Device Id' },
  ]
  const onSearch = (data: any) => {
    const req = clean(data)
    lazySearchApi({ mid: mid ? mid : undefined, ...req })
  }

  const handleDelink = (data: any, index: number) => {
    setLoadingIndex(index)
    data?.status === 'ACTIVE' && lazyDelinkApi(data?.dsn, data?.mid, data?.deviceType)
  }

  const isUnlinkAccesible = isAccessible(accessConfig, 'unlink')
  const keys = (isUnlinkAccesible ? headTitles : headTitlesWithoutAction).map((elm) => {
    return Object.keys(elm)[0]
  })

  const onUpdateStatusPress = (item: any, selectedState) => {
    lazyUpdateDeviceStatus(item, selectedState)
  }

  const renderItem = ({ item, index }) => {
    return (
      <DeviceListItem
        item={item}
        onUpdateStatusPress={onUpdateStatusPress}
        sizeConfig={isUnlinkAccesible ? [1, 0.8, 1.2, 0.8, 1.4, 1.2] : [1, 1, 1, 1, 1]}
        index={index}
        keys={keys}
      />
    )
  }

  const renderList = () => {
    if (isLoading) {
      return (
        <TextNew
          textAlign={'center'}
          title={'Loading...'}
          color={'BLACK_21'}
          fontFamily={'jiotype-bold'}
          fontSize={16}
        />
      )
    } else if (deviceMappings.length < 1) {
      return (
        <TextNew type={'body16'} textAlign="center">
          {'No Devices found'}
        </TextNew>
      )
    }
    return (
      <List
        keyExtractor={keyExtractor}
        headTitle={isUnlinkAccesible ? headTitles : headTitlesWithoutAction}
        numOfColumns={isUnlinkAccesible ? 6 : 5}
        sizeConfig={isUnlinkAccesible ? [1, 0.8, 1.2, 0.8, 1.4, 1.2] : [1, 1, 1, 1, 1]}
        data={deviceMappings}
        listTitle={'Voicebox List'}
        onPress={handleDelink}
        isActionLoading={state === 'pending'}
        loadingIndex={loadingIndex}
        renderItem={renderItem}
        tableWidth={Math.max(1200, Dimensions.get('window').width - 224 - 82)}
        headerTextStyle={{ fontSize: 12, fontFamily: 'jioType-bold', textTransform: 'capitalize' }}
      />
    )
  }

  const onSubmitClicked = () => {
    const data = formRef?.current?.getValues() ?? {}
    if (data) {
      onSearch({ ...data, deviceType: selectedDeviceType })
    }
  }

  const handleActions = (props: ActionButtonProps) => {
    formActionButtonRef.current = props
    setIsValidDevice(!!props.values.deviceid)
    return <></>
  }

  const handleMidSearch = (data: any) => {
    onSearch({ mid: data?.search, deviceType: selectedDeviceType })
  }

  const handleOnChange = (val: any) => {
    const regex = /^[0-9]*$/
    val?.length === 15 && regex.test(val) ? setMid(val) : setMid('')
  }

  return (
    <Box flex={1}>
      <TextNew title={'Devices Mapping'} fontSize={28} fontWeight={'600'} color={'#5a5c69' as ColorOptions} />
      <Spacer direction="vertical" size={24} />
      <Box flexDirection={'row'}>
        <SearchFilter
          onChange={handleOnChange}
          onSearch={(data: any) => handleMidSearch(data)}
          placeholder={'Search By MID'}
        />
        <Spacer direction="horizontal" size={16} />
        <Dropdown
          minWidth={150}
          data={deviceType}
          fieldName="Device"
          title="Device Type"
          value={selectedDeviceType}
          setFieldValue={(_, value) => {
            setSelectedDeviceType(value)
          }}
        />
      </Box>

      <Spacer direction="vertical" size={24} />
      <Box flexDirection={'row'} align={'center'} flexWrap={'wrap'}>
        <Form
          ref={formRef}
          initialValues={{ deviceType: 'VOICEBOX' }}
          onFormSubmit={onFormSubmit}
          formData={searchFormData()}
          actionButtons={handleActions}
          container={FormBox}
        />
        <Spacer direction={'horizontal'} size={8} />
        <SearchButton
          {...formActionButtonRef.current}
          onProceedPress={onSubmitClicked}
          isLoading={isLoading}
          isValidDevice={isValidDevice}
          mid={mid}
        />
      </Box>
      <Spacer direction="vertical" size={24} />
      {renderList()}
      <ModalDialog type={'Centered'} isFullWidth={true} show={updateDeviceHandler?.state === 'pending'}>
        <LoaderNew type={'large'} color={'PRIMARY'} />
      </ModalDialog>
    </Box>
  )
}

export default Devices
const styles = {
  formContainer: {
    underlayColor: 'WHITE',
    pv: 16,
  } as BoxProps,
}
const DEVICE_TYPES = [
  { id: 1, value: 'POS' },
  { id: 2, value: 'VOICEBOX' },
]
